import { AccessToken, Configuration } from '@shared/sdk-v1';

const AUTH_KEYS = [
  'accessToken',
  'user',
  'tokenExpiration',
  'roles',
  'pagePermissions',
];

const clearAuthAndRedirect = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  window.location.href = '/';
};

// 401s from these endpoints reflect external/third-party credentials being
// verified (the user's own login, or a warehouse/OnePlus portal login proxied
// by the backend) — NOT the admin's own session expiring. They must not force a
// logout/redirect, so the calling component can surface an inline error instead.
const SESSION_AGNOSTIC_401_ENDPOINTS = [
  /\/CourierUsers\/login\b/,
  /\/CourierSettings\/connectWarehouse\b/,
  /\/CourierSettings\/connectOnePlus\b/,
  // Overlord-only endpoint: LoopBack returns 401 (not 403) for role denials,
  // so a non-Overlord reaching it must not be treated as an expired session.
  /\/Couriers\/owner-contacts\b/,
  // Role-gated preview. Its ACL is kept in step with the Communication page's
  // allowed roles, but a future drift between the two must surface as an
  // inline preview error rather than silently logging the admin out.
  /\/MailBanks\/previewCommunication\b/,
  // Role-gated: only Administrator / Warehouse Administrator / Super
  // Administrator / Overlord may clear a package's assignment-review flag. The
  // button is already hidden from everyone else, but a role denial here must
  // show an inline error rather than log a working attendant out mid-shift.
  /\/CourierPackages\/[^/]+\/confirm-assignment\b/,
  // Role-gated partner endpoints. Smart Scan hits check-partnership for every
  // courier-owned scan and sub-courier-customers to populate the partner
  // recipient list, so a role denial here would log a working attendant out
  // mid-shift. The caller treats a failure as "couldn't verify" and offers a
  // retry instead — see utils/partnerMode.ts.
  // Resolved during the courier bootstrap in fetchCourier, which runs on every
  // page load — including before anyone has signed in. The endpoint's ACL is
  // $authenticated, so a logged-out visitor always gets a 401 here. Treating
  // that as an expired session redirected to '/', which remounted App, which
  // probed again: a permanent reload loop that made a freshly set-up admin
  // site unreachable to anyone without a cached token.
  /\/PartnerLinks\/is-partner-linked\b/,
  /\/PartnerLinks\/check-partnership\b/,
  /\/PartnerLinks\/sub-courier-customers\b/,
  // A rejected passkey assertion is a failed sign-in attempt, not an expired
  // session — logging the user out here would wipe the error before they could
  // read it.
  /\/CourierUsers\/passkey\/login-(options|verify)\b/,
  // Message templates are Overlord/Administrator-only (plus Cashier read), and
  // a cross-realm write is refused outright. Any of those denials arrives as a
  // 401, which must show an inline error instead of ending the session.
  // Covers the collection, /sendTest and /defaults.
  /\/NotificationTemplates\b/,
  // Role-gated: only Overlord / Administrator / Warehouse Administrator may
  // pull the scan bonus report. LoopBack returns 401 (not 403) for role
  // denials, so a Cashier hitting this must see an inline error, not get
  // logged out mid-shift.
  /\/PackageScanEvents\/bonus-report\b/,
  // Shell-level fetches (PartnerComplianceBanner / PartnerBlockedModal poll
  // these on every authenticated page via usePartnerComplianceStatus, and
  // NotificationDropdown-driven navigation hits incoming contracts). A role
  // denial here must not log the user out mid-session.
  /\/PartnerKycs\/my-compliance\b/,
  /\/PartnerContracts\/incoming\b/,
  // Role-gated repair run behind the Warehouse Hub "Resync" button (Overlord /
  // Administrator / Warehouse Administrator). Custom page permissions can put
  // someone else on that page, and a role denial arrives as a 401 — show an
  // inline error rather than ending the session mid-shift.
  /\/CourierPackages\/backfill-sub-courier-status\b/,
  // Fulfillment is role-gated to Overlord / Administrator / Warehouse
  // Administrator on both the route (App.tsx) and, as of courierapi ORD-222 and
  // ORD-227, the model ACLs. The two must be listed here anyway, because
  // AdminProtectedRoute passes `allowedRoles={hasExplicitPermission ? [] :
  // allowedRoles}` — an explicit custom page permission BYPASSES the route's
  // role list entirely, and `fulfillment-partners` / `fulfillment-orders` are
  // both grantable page keys (config/pagePermissions.ts). So a courier can hand
  // a Cashier the Fulfillment Orders page, useFulfillmentOrders fires
  // fulfillmentOrderFind on mount, and the ACL answers 401 — which without
  // these two lines wipes the session on page load and bounces to '/', every
  // time they open the page they were granted.
  /\/FulfillmentOrders\b/,
  /\/FulfillmentPartners\b/,
  // The global tariff table (Tariff Codes page) is Overlord-only to write and
  // to list; reads used by the Customs Estimator are public. A role denial on
  // either must surface inline, never end the session.
  /\/TariffCodes\b/,
  /\/TariffCategories\b/,
];

// Same idea, but only for one HTTP verb — where reads of the same URL must
// still treat a 401 as an expired session.
const SESSION_AGNOSTIC_401_METHOD_ENDPOINTS: { method: string; pattern: RegExp }[] = [
  // Package delete. courier-package.json carries `$owner DENY deleteById` so a
  // customer cannot delete their own package, and staff routinely OWN packages
  // (admin-created / unassigned ones fall back to the admin's own user). The
  // staff roles are explicitly allowed now, but a role denial here must show
  // "Failed to delete package" — it used to log the admin out and leave the
  // package in place.
  { method: 'DELETE', pattern: /\/CourierPackages\/[^/?]+(\?|$)/ },
];

const isSessionAgnostic401 = (url: string, method = 'GET') =>
  SESSION_AGNOSTIC_401_ENDPOINTS.some((pattern) => pattern.test(url)) ||
  SESSION_AGNOSTIC_401_METHOD_ENDPOINTS.some(
    (endpoint) =>
      endpoint.method === method.toUpperCase() && endpoint.pattern.test(url)
  );

const expiredTokenMiddleware = {
  post: async (context: { response: Response; init?: RequestInit }) => {
    if (
      context.response.status === 401 &&
      !isSessionAgnostic401(context.response.url || '', context.init?.method)
    ) {
      clearAuthAndRedirect();
    }
    return context.response;
  },
};

/**
 * @param extraHeaders Per-call request headers merged into the defaults, for
 * backend flags that aren't part of any request body — e.g.
 * `x-skip-status-alert` on a package save, which reaches
 * `ctx.options.skipStatusAlert` in the CourierPackage observers.
 */
export const apiConfig = (
  forceToken?: string,
  extraHeaders?: Record<string, string>
) => {
  if (forceToken) {
    return new Configuration({
      basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
      accessToken: forceToken,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${forceToken}`,
        'X-API-Key': `0d7f4b1b-0d7f-4b1b-9d0f-8b0f3f7c6f2a`,
        ...extraHeaders,
      },
    });
  }

  const tokenExpiration = localStorage.getItem('tokenExpiration');
  if (tokenExpiration && new Date(tokenExpiration) <= new Date()) {
    clearAuthAndRedirect();
    return new Configuration({
      basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
    });
  }

  const accessTokenString = localStorage.getItem('accessToken');
  const accessToken: AccessToken = accessTokenString
    ? JSON.parse(accessTokenString)
    : undefined;
  return new Configuration({
    basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
    accessToken: accessToken?.id ?? undefined,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `${accessToken?.id}`,
      'X-API-Key': `0d7f4b1b-0d7f-4b1b-9d0f-8b0f3f7c6f2a`,
      ...extraHeaders,
    },
    middleware: [expiredTokenMiddleware],
  });
};
